import { useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { CompanyFeatures } from '@fametrue/shared';
import { useErrorMessage } from '../lib/useErrorMessage';
import { useCompanies, useCreateCompany, useUpdateFeatures } from './api';

const LEVELS: (keyof CompanyFeatures)[] = ['insight', 'decision', 'intelligence'];

/**
 * Anagrafica company e feature flag di piano.
 *
 * I flag decidono quali livelli il cliente vede, e sono applicati lato server:
 * togliere Decision qui significa che i blocchi Diagnosi non escono più dall'API
 * per gli utenti di quella company, non solo che la tab sparisce.
 */
export function CompaniesPanel(): ReactNode {
  const { t } = useTranslation();
  const companies = useCompanies();
  const createCompany = useCreateCompany();
  const updateFeatures = useUpdateFeatures();
  const describeError = useErrorMessage();

  const [name, setName] = useState('');

  function onSubmit(event: FormEvent): void {
    event.preventDefault();
    if (name.trim().length === 0) return;
    createCompany.mutate(name.trim(), { onSuccess: () => setName('') });
  }

  return (
    <section className="admin-card">
      <header className="admin-card-head">
        <h2>{t('admin.companies.title')}</h2>
        <p>{t('admin.companies.subtitle')}</p>
      </header>

      <form className="admin-inline-form" onSubmit={onSubmit}>
        <input
          type="text"
          value={name}
          placeholder={t('admin.companies.namePlaceholder')}
          onChange={(e) => setName(e.target.value)}
        />
        <button type="submit" className="admin-btn primary" disabled={createCompany.isPending}>
          {t('admin.companies.create')}
        </button>
      </form>

      {createCompany.isError ? (
        <p className="admin-alert error">{describeError(createCompany.error)}</p>
      ) : null}

      <table className="admin-table">
        <thead>
          <tr>
            <th>{t('admin.companies.name')}</th>
            <th>{t('admin.companies.levels')}</th>
          </tr>
        </thead>
        <tbody>
          {(companies.data ?? []).map((company) => (
            <tr key={company.id}>
              <td>
                <div className="admin-strong">{company.name}</div>
                <code className="admin-mono">{company.slug}</code>
              </td>
              <td>
                <div className="admin-toggles">
                  {LEVELS.map((level) => (
                    <label key={level} className="admin-toggle">
                      <input
                        type="checkbox"
                        checked={company.features[level]}
                        disabled={updateFeatures.isPending}
                        onChange={(e) =>
                          updateFeatures.mutate({
                            companyId: company.id,
                            features: { ...company.features, [level]: e.target.checked },
                          })
                        }
                      />
                      {t(`levels.${level}`)}
                    </label>
                  ))}
                </div>
              </td>
            </tr>
          ))}
          {companies.data?.length === 0 ? (
            <tr>
              <td colSpan={2} className="admin-empty">
                {t('admin.companies.empty')}
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </section>
  );
}
